import localforage from 'localforage';
import { DateTime } from 'luxon';

export class CacheUtils {
  static LastAccessedSuffix = '_PPLastAccessed';

  private static getLastAccessedKey = (key: string) =>
    key + this.LastAccessedSuffix;
  private static getObjectKey = (lastAccessedKey: string) =>
    lastAccessedKey.replace(this.LastAccessedSuffix, '');
  private static isLastAccessedKey = (key: string) =>
    key.endsWith(this.LastAccessedSuffix);

  /**
   * Determine if the given value key has expired by evaluating its corresponding lastAccessed key.
   * */
  private static async hasExpired(
    key: string,
    maxAgeMs: number,
  ): Promise<boolean> {
    // fetch the cached timestamp (if any)
    const timestamp = await localforage.getItem<string>(
      this.getLastAccessedKey(key),
    );
    // attempt to convert the timestamp string to an ISO DateTime.
    const timestampDT = DateTime.fromISO(timestamp ?? '', { zone: 'utc' });
    // the cached object has expired if the timestamp can't be resolved,
    //   OR it is older than our defined maxCacheAgeMs.
    return (
      !timestampDT.isValid || // not a valid timestamp
      DateTime.utc().diff(timestampDT).milliseconds > maxAgeMs
    ); // too old
  }

  /**
   * Set the last accessed time of this key to now.
   */
  private static async updateLastAccessed(key: string): Promise<void> {
    const timestamp = DateTime.utc().toISO();
    await localforage.setItem<string>(this.getLastAccessedKey(key), timestamp);
  }

  /**
   * Get a value from storage.
   * */
  static async get<T>(key: string): Promise<T | undefined> {
    const value = await localforage.getItem<T>(key);
    if (value === null) {
      return undefined;
    } else {
      await this.updateLastAccessed(key);
      return value;
    }
  }

  /**
   * Add a value to storage.
   * */
  static async set<T>(key: string, value: T): Promise<T> {
    await Promise.all([
      localforage.setItem<T>(key, value),
      this.updateLastAccessed(key),
    ]);
    return value;
  }

  /**
   * Remove a value from storage.
   */
  static async remove(key: string): Promise<void> {
    // remove the cached value and corresponding timestamp
    await Promise.all([
      localforage.removeItem(key),
      localforage.removeItem(this.getLastAccessedKey(key)),
    ]);
  }

  /**
   * Remove all expired keys from storage.
   * */
  static async evictExpiredKeys(maxAgeMs: number): Promise<void> {
    // fetch all cached keys.
    const keys = await localforage.keys();
    // filter to only 'lastAccessed` keys to prevent removal of objects not managed by CacheUtils.
    for (const key of keys
      .filter(this.isLastAccessedKey)
      .map(this.getObjectKey)) {
      // if cached object has expired remove it.
      if (await this.hasExpired(key, maxAgeMs)) await this.remove(key);
    }
  }
}
